import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { WorkOutline as WorkOutlineIcon } from '@mui/icons-material';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import useInterviewerAccess from '../../hooks/useInterviewerAccess';

/**
 * Always-visible top-bar interviewer control (not a 7th icon in the primary row).
 * Candidates see "Become interviewer" → apply. After they apply, "Interviewer" → dashboard.
 */
export default function TopBarInterviewerLink() {
  const location = useLocation();
  const { enabled: marketplaceEnabled } = useFeatureFlag('interviewer_marketplace');
  const { canAccessDashboard } = useInterviewerAccess();

  if (!marketplaceEnabled) return null;

  const to = canAccessDashboard ? '/interviewer/dashboard' : '/marketplace/apply';
  const label = canAccessDashboard ? 'Interviewer' : 'Become interviewer';
  const isApplyCta = !canAccessDashboard;
  const active =
    location.pathname.startsWith('/marketplace/apply') ||
    location.pathname.startsWith('/interviewer/dashboard');

  return (
    <Button
      component={RouterLink}
      to={to}
      startIcon={<WorkOutlineIcon fontSize="small" />}
      data-testid="topbar-interviewer"
      data-variant={isApplyCta ? 'apply' : 'dashboard'}
      aria-current={active ? 'page' : undefined}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
        whiteSpace: 'nowrap',
        flexShrink: 0,
        minWidth: 0,
        px: { xs: 1, sm: 1.25 },
        py: 0.5,
        boxShadow: 'none',
        '& .MuiButton-startIcon': {
          display: { xs: 'none', sm: 'inherit' },
          mr: { sm: 0.75 },
        },
        ...(isApplyCta
          ? {
              color: 'secondary.dark',
              bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.secondary.main, 0.45),
              '&:hover': {
                color: 'secondary.dark',
                bgcolor: (t) => alpha(t.palette.secondary.main, 0.2),
                borderColor: 'secondary.main',
                boxShadow: 'none',
              },
            }
          : {
              color: active ? 'secondary.dark' : 'text.secondary',
              bgcolor: 'transparent',
              border: '1px solid',
              borderColor: active ? 'secondary.main' : 'divider',
              '&:hover': {
                color: 'secondary.dark',
                borderColor: 'secondary.main',
                bgcolor: (t) => alpha(t.palette.secondary.main, 0.08),
              },
            }),
      }}
    >
      {label}
    </Button>
  );
}
